import { useState, useEffect, useCallback } from 'react';
import { ApiError } from '../lib/api';
import { recoveryApi, REMINDER_STAGES, type Defaulter } from '../lib/recoveryApi';
import { DataTable, type Column } from '../components/DataTable';

const rupees = (p: number) => '₹' + (p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * The Fee Recovery pipeline — the defaulter worklist plus a laddered, multi-select reminder send.
 * Reuses the defaulter report for WHO owes and the notification outbox for the send; adds the
 * "remind exactly these families, at this stage, now" action and a record of who was reminded.
 */
export function RecoveryPage() {
  const [rows, setRows] = useState<Defaulter[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [stage, setStage] = useState<string>('Gentle');
  const [email, setEmail] = useState(true);
  const [sms, setSms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  /**
   * Columns.
   *
   * ⚠ Defined inside the component because the first one renders a checkbox bound to `selected`.
   * Hoisting it to module scope would mean the checkbox never re-rendered when a row was ticked.
   */
  const columns: Column<Defaulter>[] = [
    {
      id: 'pick', header: '',
      value: (r) => (selected.has(r.studentId) ? '1' : '0'),
      cell: (r) => (
        <input
          type="checkbox"
          checked={selected.has(r.studentId)}
          onChange={() => toggle(r.studentId)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      id: 'student', header: 'Student', sortable: true, primary: true,
      // ⚠ Admission number is in the sort/search value as well as the name, so typing "2026-0115"
      // finds the row. A clerk holding a receipt has the number, not the spelling.
      value: (r) => `${r.studentName} ${r.admissionNumber}`,
      cell: (r) => (
        <>
          <div style={{ fontWeight: 600 }}>{r.studentName}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {r.admissionNumber}{r.classLabel ? ` · ${r.classLabel}` : ''}
          </div>
        </>
      ),
    },
    {
      id: 'outstanding', header: 'Outstanding', align: 'right', sortable: true,
      // Paise, not the formatted string — otherwise ₹9,000 sorts above ₹80,000.
      value: (r) => r.outstandingPaise,
      cell: (r) => (
        <div style={{ fontWeight: 600, color: '#b91c1c', fontVariantNumeric: 'tabular-nums' }}>
          {rupees(r.outstandingPaise)}
          <div style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 400 }}>
            {r.overdueInvoices} invoice{r.overdueInvoices === 1 ? '' : 's'}
          </div>
        </div>
      ),
    },
    {
      id: 'since', header: 'Overdue since', sortable: true,
      // Raw ISO sorts correctly; the displayed "26/6/2026" would sort as text.
      value: (r) => r.earliestDueDate ?? '',
      cell: (r) => (
        <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
          {r.earliestDueDate ? new Date(r.earliestDueDate).toLocaleDateString('en-IN') : '—'}
        </span>
      ),
    },
    {
      id: 'reminded', header: 'Last reminded', sortable: true, hideSm: true,
      // ⚠ Never-reminded sorts FIRST with an empty string — which is who you want to chase next.
      value: (r) => r.lastRemindedAt ?? '',
      cell: (r) => (
        <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
          {r.lastRemindedAt
            ? <>{new Date(r.lastRemindedAt).toLocaleDateString('en-IN')}{' '}
                <span style={{ color: 'var(--ink-3)' }}>· {r.lastStage} · {r.reminderCount}×</span></>
            : <span style={{ color: 'var(--ink-3)' }}>never</span>}
        </span>
      ),
    },
  ];

  const load = useCallback(() => {
    recoveryApi.worklist().then((r) => { setRows(r); setSelected(new Set()); }).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (!rows) return;
    setSelected(selected.size === rows.length ? new Set() : new Set(rows.map((r) => r.studentId)));
  };

  const send = async () => {
    if (selected.size === 0) return;
    setBusy(true); setMsg(null);
    try {
      const res = await recoveryApi.remind({ studentIds: [...selected], stage, sendEmail: email, sendSms: sms });
      setMsg(`Sent ${res.sent} reminder${res.sent === 1 ? '' : 's'} (${stage}).`);
      load();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Could not send reminders.');
    } finally { setBusy(false); }
  };

  const totalDue = rows?.reduce((s, r) => s + r.outstandingPaise, 0) ?? 0;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Fees</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Fee recovery</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: 640 }}>
          Every family with overdue fees, worst first. Pick who to chase, choose how firmly, and send —
          each reminder is logged so you never chase the same family twice by accident.
        </p>
      </div>

      {rows === null && <p style={{ color: 'var(--ink-3)' }}>Loading…</p>}
      {rows !== null && rows.length === 0 && <p style={{ color: 'var(--ink-3)', fontSize: 14 }}>No overdue fees. Nothing to recover.</p>}

      {rows !== null && rows.length > 0 && (
        <>
          {/* action bar */}
          <div className="card" style={{ padding: 14, marginBottom: 14, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              <strong>{selected.size}</strong> selected · <strong>{rupees(totalDue)}</strong> overdue across {rows.length} families
              {rows.length > 0 && (
                <button
                  className="btn btn-sm"
                  style={{ marginLeft: 10 }}
                  onClick={toggleAll}
                >
                  {/* ⚠ Selects every defaulter, not just the visible page.
                      The send acts on the whole selection, so a button that silently meant "this
                      page only" would under-send and nobody would notice until the money did not
                      arrive. */}
                  {selected.size === rows.length ? 'Clear selection' : `Select all ${rows.length}`}
                </button>
              )}
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                Firmness
                <select className="input" value={stage} onChange={(e) => setStage(e.target.value)} style={{ marginLeft: 6, width: 'auto', display: 'inline-block' }}>
                  {REMINDER_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} /> Email</label>
              <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={sms} onChange={(e) => setSms(e.target.checked)} /> SMS</label>
              <button className="btn btn-accent" disabled={busy || selected.size === 0} onClick={send}>
                {busy ? 'Sending…' : `Remind ${selected.size || ''}`}
              </button>
            </div>
          </div>

          {msg && <div className="pill" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start', marginBottom: 12, fontSize: 13 }}>{msg}</div>}

          <DataTable<Defaulter>
            rows={rows}
            columns={columns}
            rowKey={(r) => r.studentId}

            search
            searchPlaceholder="Search by name or admission number…"

            // ⚠ CLIENT-side paging here, unlike Fees.
            //
            // The worklist is already narrowed to defaulters — a bounded set, and a school with two
            // hundred of them has a problem no software solves. More importantly, the page lets you
            // SELECT ACROSS the whole list and send in one action; server-side paging would drop a
            // selection every time the page changed.
            pageSize={25}

            empty={{
              title: 'Nobody is overdue',
              body: 'Every family is up to date. Nothing to chase.',
            }}
          />

        </>
      )}
    </div>
  );
}
